import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight, Server, CloudCog, Network, ShieldCheck, Gauge, Layers, LifeBuoy,
  ClipboardCheck, Search,
} from "lucide-react";
import PageHead from "@/components/PageHead";
import Reveal, { SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import CTASection from "@/components/CTASection";

const HERO_IMG = "/hero-datacenter.jpg";

const PILLARS = [
  {
    icon: Server,
    title: "Servidores & Infraestrutura",
    desc: "Windows Server e Linux: instalação, hardening, alta disponibilidade e administração completa do parque de servidores.",
    to: "/servicos#windows-linux",
    testId: "pillar-servers",
    span: "md:col-span-8",
  },
  {
    icon: CloudCog,
    title: "Nuvem & Migração Híbrida",
    desc: "Azure, Microsoft 365 e Entra ID em arquiteturas híbridas seguras.",
    to: "/servicos#azure-cloud",
    testId: "pillar-cloud",
    span: "md:col-span-4",
  },
  {
    icon: Network,
    title: "Redes & Segurança",
    desc: "VLANs, firewalls e VPNs corporativas com segmentação e controle de acessos.",
    to: "/servicos#networks-security",
    testId: "pillar-network",
    span: "md:col-span-4",
  },
  {
    icon: ShieldCheck,
    title: "Backup & Continuidade",
    desc: "Estratégias de backup validadas e planos de recuperação para o negócio não parar.",
    to: "/servicos#backup-continuity",
    testId: "pillar-backup",
    span: "md:col-span-4",
  },
  {
    icon: ClipboardCheck,
    title: "Auditoria & Suporte Especializado",
    desc: "Diagnósticos técnicos, inventário de infraestrutura e suporte N2/N3 preventivo e reativo.",
    to: "/servicos#audits-support",
    testId: "pillar-support",
    span: "md:col-span-4",
  },
];

const BENEFITS = [
  {
    icon: Gauge,
    title: "Maior disponibilidade",
    desc: "Ambientes dimensionados e monitorados para reduzir paradas não planejadas e manter a operação de pé.",
  },
  {
    icon: ShieldCheck,
    title: "Segurança em camadas",
    desc: "Controle de acessos, segmentação de rede e proteção perimetral alinhados a boas práticas de mercado.",
  },
  {
    icon: Layers,
    title: "Escalabilidade planejada",
    desc: "Infraestrutura preparada para o crescimento da empresa, sem retrabalho e sem desperdício de recursos.",
  },
  {
    icon: LifeBuoy,
    title: "Continuidade operacional",
    desc: "Backups testados e planos de contingência para que imprevistos não interrompam o negócio.",
  },
];

const ORG_JSONLD = {
  "@context": "https://schema.org",
  "@type": "ITService",
  name: "IPRS Soluções em TI",
  description:
    "Consultoria corporativa em infraestrutura de TI: servidores Windows e Linux, redes, segurança, nuvem híbrida Azure e suporte especializado.",
  areaServed: "BR",
  serviceType: [
    "IT Infrastructure Consulting",
    "Network Security",
    "Cloud Migration",
    "Server Administration",
  ],
  email: "contato@iprsinf.com.br",
  telephone: "+55-21-99159-2603",
  sameAs: [
    "https://www.instagram.com/iprssolucoes/",
    "https://web.facebook.com/iprsinfo",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    telephone: "+55-21-99159-2603",
    email: "contato@iprsinf.com.br",
    contactType: "customer service",
    availableLanguage: "Portuguese",
  },
};

function HeroLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        className="block"
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Home() {
  const heroImgRef = useRef(null);
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 600], [0, 64]);

  return (
    <div>
      <PageHead
        title="IPRS Soluções em TI | Infraestrutura Corporativa & Consultoria Especializada"
        description="Consultoria, implementação e gestão de infraestrutura de TI corporativa: servidores Windows e Linux, redes, nuvem Azure e suporte especializado."
        path="/"
        jsonLd={ORG_JSONLD}
      />

      {/* Hero */}
      <section className="relative pt-28 lg:pt-40 pb-16 lg:pb-24 overflow-hidden">
        <div className="absolute inset-0 grid-lines" aria-hidden="true" />
        <div className="absolute -top-32 right-0 w-[560px] h-[560px] rounded-full bg-pale blur-3xl opacity-70" aria-hidden="true" />
        <div className="container-x relative grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="inline-flex items-center gap-2 font-mono text-xs tracking-wide text-graphite/70 bg-white border border-line rounded-full px-4 py-1.5"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-techblue" />
              Consultoria e infraestrutura de TI corporativa
            </motion.p>

            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-graphite leading-[1.12]">
              <HeroLine delay={0.15}>Sua infraestrutura</HeroLine>
              <HeroLine delay={0.27}>de TI nas mãos</HeroLine>
              <HeroLine delay={0.39}>
                <span className="text-techblue">de especialistas</span>
              </HeroLine>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.55 }}
              className="mt-6 text-base sm:text-lg text-steel leading-relaxed max-w-xl"
            >
              Projetamos, implementamos e administramos servidores, redes e ambientes em
              nuvem para empresas que não podem parar.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.68 }}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <Link
                to="/contato"
                data-testid="cta-assessment-hero"
                className="inline-flex items-center justify-center gap-1.5 bg-techblue hover:bg-techblue-hover text-white text-sm font-semibold px-7 py-3.5 rounded-md transition-colors duration-200"
              >
                Solicitar avaliação técnica
                <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link
                to="/servicos"
                data-testid="cta-solutions-hero"
                className="inline-flex items-center justify-center gap-1.5 border border-graphite/20 hover:border-techblue hover:text-techblue text-graphite text-sm font-semibold px-7 py-3.5 rounded-md transition-colors duration-200"
              >
                Conhecer soluções
              </Link>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.85 }}
              className="mt-8 font-mono text-[11px] tracking-wide text-steel/80 uppercase"
            >
              Consultoria • Implementação • Administração • Modernização • Suporte
            </motion.p>
          </div>

          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative"
            >
              <motion.div style={{ y: parallaxY }} className="relative">
                <div className="relative rounded-xl overflow-hidden border border-line shadow-[0_24px_60px_-24px_rgba(37,44,51,0.35)]">
                  <img
                    ref={heroImgRef}
                    src={HERO_IMG}
                    alt="Servidores corporativos em data center com iluminação azul"
                    className="w-full h-[340px] sm:h-[420px] object-cover"
                    loading="eager"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-graphite/60 via-transparent to-transparent" />
                </div>
                <div className="absolute -bottom-6 left-4 right-4 sm:left-6 sm:right-auto bg-white border border-line rounded-lg shadow-lg px-5 py-4 flex items-center gap-4">
                  <span className="w-2.5 h-2.5 rounded-full bg-techblue animate-pulse" />
                  <div>
                    <p className="text-sm font-semibold text-graphite">Ambientes monitorados</p>
                    <p className="text-xs text-steel mt-0.5">Windows Server • Linux • Azure • Hyper-V</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* Assessment banner */}
      <section className="py-16 sm:py-24" id="assessment">
        <div className="container-x">
          <Reveal>
            <div className="bg-pale border border-line rounded-xl px-6 sm:px-10 py-10 sm:py-12 flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex-1">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue">Avaliação técnica</p>
                <h2 className="mt-3 text-xl sm:text-2xl lg:text-3xl font-bold text-graphite tracking-tight">
                  Diagnóstico completo do seu ambiente de TI
                </h2>
                <p className="mt-3 text-steel leading-relaxed max-w-2xl">
                  Mapeamos servidores, redes, backups e acessos da sua operação e entregamos um
                  panorama objetivo de riscos e oportunidades de melhoria.
                </p>
              </div>
              <Link
                to="/contato"
                data-testid="cta-assessment-banner"
                className="inline-flex items-center justify-center gap-1.5 bg-graphite hover:bg-ink text-white text-sm font-semibold px-7 py-3.5 rounded-md transition-colors duration-200 shrink-0"
              >
                <Search className="w-4 h-4" />
                Agendar diagnóstico
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Service pillars bento */}
      <section className="py-16 sm:py-24 lg:py-28 bg-mist border-y border-line">
        <div className="container-x">
          <SectionHeading
            eyebrow="Pilares de serviço"
            title="Tudo o que sua infraestrutura precisa, em um só lugar"
            text="Da sala de servidores à nuvem: cobertura completa em consultoria, implementação, administração e suporte."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.testId} delay={i * 0.06} className={p.span}>
                <Link
                  to={p.to}
                  data-testid={p.testId}
                  className="group h-full flex flex-col bg-white border border-line rounded-xl p-7 hover:border-techblue/50 hover:shadow-[0_16px_40px_-20px_rgba(7,136,248,0.35)] transition-shadow duration-200"
                >
                  <span className="w-11 h-11 rounded-lg bg-pale flex items-center justify-center text-techblue group-hover:bg-techblue group-hover:text-white transition-colors duration-200">
                    <p.icon className="w-5 h-5" />
                  </span>
                  <h3 className="mt-5 text-lg sm:text-xl font-semibold text-graphite">{p.title}</h3>
                  <p className="mt-2.5 text-sm text-steel leading-relaxed flex-1">{p.desc}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-techblue">
                    Ver detalhes
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow="Benefícios para o negócio"
            title="TI estável, segura e pronta para crescer"
            text="Infraestrutura bem projetada deixa de ser fonte de problemas e passa a sustentar a operação."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.06} className="h-full">
                <div className="h-full border-l-2 border-techblue/70 bg-white pl-5 pr-4 py-6">
                  <b.icon className="w-6 h-6 text-techblue" />
                  <h3 className="mt-4 text-base font-semibold text-graphite">{b.title}</h3>
                  <p className="mt-2 text-sm text-steel leading-relaxed">{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies strip */}
      <section className="py-16 sm:py-20 bg-graphite">
        <div className="container-x flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
          <div className="max-w-xl">
            <Reveal>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-techblue">Tecnologias</p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-3 text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Plataformas e padrões de mercado
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-3 text-white/60 text-sm sm:text-base leading-relaxed">
                Trabalhamos com ecossistemas consolidados: Microsoft, ambientes Linux e open
                source, redes e infraestrutura de data center.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <Link
              to="/tecnologias"
              data-testid="home-tech-link"
              className="inline-flex items-center gap-1.5 border border-white/25 hover:border-techblue hover:text-techblue text-white text-sm font-semibold px-7 py-3.5 rounded-md transition-colors duration-200"
            >
              Ver stack completo
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Projects preview */}
      <section className="py-16 sm:py-24 lg:py-28">
        <div className="container-x">
          <SectionHeading
            eyebrow="Projetos"
            title="Desafio, solução e resultado — documentados com transparência"
            text="Nosso portfólio apresenta a estrutura real de cada trabalho: o desafio técnico, a solução arquitetada e o resultado obtido."
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { kicker: "CASE 01", theme: "Windows Server e Active Directory" },
              { kicker: "CASE 02", theme: "Recuperação de Serviços Críticos" },
              { kicker: "CASE 03", theme: "Microsoft Azure e Linux" },
            ].map((c, i) => (
              <Reveal key={c.kicker} delay={i * 0.08} className="h-full">
                <div className="h-full border-l-2 border-techblue/70 bg-mist pl-5 pr-4 py-6">
                  <p className="font-mono text-xs tracking-wide text-techblue">{c.kicker}</p>
                  <h3 className="mt-2 text-base font-semibold text-graphite leading-snug">{c.theme}</h3>
                  <p className="mt-2 text-sm text-steel leading-relaxed">
                    Desafio, solução e resultado documentados.
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-8">
              <Link
                to="/projetos"
                data-testid="home-projects-link"
                className="inline-flex items-center gap-1.5 border border-graphite/20 hover:border-techblue hover:text-techblue text-graphite text-sm font-semibold px-7 py-3.5 rounded-md transition-colors duration-200"
              >
                Ver página de projetos
                <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
